import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { tipsApi } from '../lib/api';
import { formatCurrency, formatRelativeTime } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Heart, Send, Trophy, ArrowDownLeft, ArrowUpRight, Search, Loader2, Star, MessageCircle } from 'lucide-react';

const TIP_AMOUNTS = [100, 500, 1000, 2000, 5000, 10000, 20000, 50000];

const TippingPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('send');
  const [received, setReceived] = useState({ tips: [], total_received: 0 });
  const [sent, setSent] = useState({ tips: [], total_sent: 0 });
  const [leaderboard, setLeaderboard] = useState([]);
  const [creators, setCreators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedCreator, setSelectedCreator] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [tipMessage, setTipMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [receivedRes, sentRes, leaderboardRes, creatorsRes] = await Promise.all([
        tipsApi.getReceived(50),
        tipsApi.getSent(50),
        tipsApi.getLeaderboard(),
        tipsApi.getCreators(),
      ]);
      setReceived(receivedRes.data);
      setSent(sentRes.data);
      setLeaderboard(leaderboardRes.data);
      setCreators(creatorsRes.data);
    } catch (error) {
      toast.error('Failed to load tipping data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTip = async () => {
    if (!selectedCreator || !tipAmount || parseFloat(tipAmount) <= 0) {
      toast.error('Select a creator and enter an amount');
      return;
    }
    setIsSending(true);
    try {
      const res = await tipsApi.send(selectedCreator.wallet_id, parseFloat(tipAmount), tipMessage || null, 'general');
      toast.success(`Sent ${formatCurrency(parseFloat(tipAmount))} to ${res.data.recipient}!`);
      setSelectedCreator(null);
      setTipAmount('');
      setTipMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send tip');
    } finally {
      setIsSending(false);
    }
  };

  const filteredCreators = creators.filter(c =>
    c.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6">
          <Skeleton className="h-8 w-48" /><Skeleton className="h-48 w-full rounded-xl" /><Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  const TipRow = ({ tip, direction }) => (
    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
      <img src={(direction === 'received' ? tip.from_avatar : tip.to_avatar) || ''} alt="" className="w-10 h-10 rounded-full object-cover bg-white/10" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{direction === 'received' ? tip.from_name : tip.to_name}</p>
        {tip.message && <p className="text-xs text-muted-foreground truncate">"{tip.message}"</p>}
        <p className="text-xs text-muted-foreground">{formatRelativeTime(tip.created_at)}</p>
      </div>
      <span className={`font-medium text-sm ${direction === 'received' ? 'text-green-500' : ''}`}>
        {direction === 'received' ? '+' : '-'}{formatCurrency(tip.amount)}
      </span>
    </div>
  );

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-24 lg:pb-0">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Heart className="w-7 h-7 inline mr-2 text-primary" />Tips & Gifts
          </h1>
          <p className="text-muted-foreground">Support creators you love</p>
        </div>

        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Stats */}
            <div className="px-6 lg:px-0 mb-6 grid grid-cols-2 gap-3">
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <ArrowDownLeft className="w-5 h-5 text-green-500 mb-2" />
                <p className="text-xs text-muted-foreground">Tips Received</p>
                <p className="text-xl font-bold text-green-500" data-testid="tips-received-total">{formatCurrency(received.total_received)}</p>
                <p className="text-xs text-muted-foreground">{received.count} tips</p>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <ArrowUpRight className="w-5 h-5 text-muted-foreground mb-2" />
                <p className="text-xs text-muted-foreground">Tips Sent</p>
                <p className="text-xl font-bold" data-testid="tips-sent-total">{formatCurrency(sent.total_sent)}</p>
                <p className="text-xs text-muted-foreground">{sent.count} tips</p>
              </div>
            </div>

            <div className="px-6 lg:px-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="send">Send Tip</TabsTrigger>
                  <TabsTrigger value="received">Received</TabsTrigger>
                  <TabsTrigger value="sent">Sent</TabsTrigger>
                </TabsList>

                <TabsContent value="send">
                  <div className="space-y-6">
                    {/* Creator Selection */}
                    {!selectedCreator ? (
                      <div>
                        <div className="relative mb-4">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search creators..." className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-0 focus:outline-none text-sm" data-testid="search-creators" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {filteredCreators.map((creator) => (
                            <button key={creator.wallet_id} onClick={() => setSelectedCreator(creator)} className="flex items-center gap-3 p-4 bg-card border border-white/10 rounded-xl hover:border-primary/30 transition-colors text-left" data-testid={`tip-creator-${creator.wallet_id}`}>
                              {creator.avatar_url ? <img src={creator.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center font-bold">{creator.full_name.charAt(0)}</div>}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{creator.full_name}</p>
                                <p className="text-sm text-muted-foreground truncate">{creator.bio || 'Creator'}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Selected Creator */}
                        <div className="bg-card border border-primary/30 rounded-xl p-4 flex items-center gap-4">
                          {selectedCreator.avatar_url && <img src={selectedCreator.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover" />}
                          <div className="flex-1"><p className="font-semibold">{selectedCreator.full_name}</p><p className="text-sm text-muted-foreground">{selectedCreator.bio || 'Creator'}</p></div>
                          <button onClick={() => setSelectedCreator(null)} className="text-sm text-primary">Change</button>
                        </div>

                        {/* Amount */}
                        <div>
                          <label className="text-sm text-muted-foreground mb-2 block">Tip Amount</label>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-2xl text-muted-foreground">₦</span>
                            <input type="number" value={tipAmount} onChange={(e) => setTipAmount(e.target.value)} placeholder="0" className="flex-1 text-3xl font-bold bg-transparent border-none focus:outline-none" data-testid="tip-amount" />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {TIP_AMOUNTS.map((a) => (
                              <button key={a} onClick={() => setTipAmount(a.toString())} className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${tipAmount === a.toString() ? 'bg-primary text-white' : 'bg-white/5 hover:bg-white/10'}`}>
                                ₦{a.toLocaleString()}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Message */}
                        <div>
                          <label className="text-sm text-muted-foreground mb-2 block">Message (optional)</label>
                          <Input value={tipMessage} onChange={(e) => setTipMessage(e.target.value)} placeholder="Love your content!" className="bg-white/5 border-white/10" data-testid="tip-message" />
                        </div>

                        <Button onClick={handleSendTip} disabled={isSending || !tipAmount} className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary" data-testid="send-tip-btn">
                          {isSending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                          Send {tipAmount ? formatCurrency(parseFloat(tipAmount)) : 'Tip'}
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="received" className="space-y-2">
                  {received.tips.length === 0 ? (
                    <div className="text-center py-12"><Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No tips received yet</p></div>
                  ) : received.tips.map((tip) => <TipRow key={tip.tip_id} tip={tip} direction="received" />)}
                </TabsContent>

                <TabsContent value="sent" className="space-y-2">
                  {sent.tips.length === 0 ? (
                    <div className="text-center py-12"><Send className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No tips sent yet</p></div>
                  ) : sent.tips.map((tip) => <TipRow key={tip.tip_id} tip={tip} direction="sent" />)}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Sidebar: Leaderboard */}
          <div className="lg:col-span-1 mt-6 lg:mt-0 px-6 lg:px-0">
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-500" />Top Tipped Creators</h3>
              <div className="space-y-3">
                {leaderboard.map((entry) => (
                  <div key={entry.wallet_id} className="flex items-center gap-3">
                    <span className={`text-lg font-bold w-6 ${entry.rank <= 3 ? 'text-yellow-500' : 'text-muted-foreground'}`}>
                      {entry.rank <= 3 ? ['', '1st', '2nd', '3rd'][entry.rank] : `#${entry.rank}`}
                    </span>
                    {entry.avatar ? <img src={entry.avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary">{entry.name?.charAt(0)}</div>}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{entry.name}</p>
                      <p className="text-xs text-muted-foreground">{entry.tip_count} tips</p>
                    </div>
                    <span className="text-green-500 font-medium text-sm">{formatCurrency(entry.total_tips)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default TippingPage;
